import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CustomerProfile.css';
import {
  Building2,
  Mail,
  Calendar,
  Loader2,
  AlertCircle,
  Shield,
  CheckCircle2,
  UserCircle,
  Store,
} from 'lucide-react';

const CustomerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    // Get ID from localStorage (Saved during Customer Login)
    const customerId = localStorage.getItem('customerId');

    if (!customerId) {
      setError('No User ID found. Please Log Out and Log In again.');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customer/profile/${customerId}`);
      setProfile(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile.');
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  /* ── Loading State ── */
  if (loading) {
    return (
      <div className="cp-root">
        <div className="cp-loading">
          <div className="cp-loading-card">
            <Loader2 className="cp-spin" size={36} />
            <span>Loading profile…</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error State ── */
  if (error) {
    return (
      <div className="cp-root">
        <div className="cp-loading">
          <div className="cp-loading-card cp-error-card">
            <AlertCircle size={40} className="cp-error-icon" />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-root">
      {/* Background blobs */}
      <div className="cp-bg-blob cp-blob-1" />
      <div className="cp-bg-blob cp-blob-2" />

      {/* ── Header ── */}
      <div className="cp-header">
        <div className="cp-header-left">
          <div className="cp-title-icon">
            <UserCircle size={26} />
          </div>
          <div>
            <h1 className="cp-title">Company Profile</h1>
            <p className="cp-subtitle">View your account details</p>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="cp-content">
        {/* Hero Card with Avatar */}
        <div className="cp-hero-card">
          <div className="cp-hero-bg" />

          <div className="cp-hero-avatar-wrap">
            <div className="cp-hero-avatar">{getInitials(profile?.fullName)}</div>
            <div className="cp-verified-badge" title="Verified Customer">
              <CheckCircle2 size={14} />
            </div>
          </div>

          <div className="cp-hero-info">
            <h2 className="cp-hero-name">{profile?.fullName || 'Customer'}</h2>
            <p className="cp-hero-email">
              <Mail size={13} /> {profile?.email || 'No email'}
            </p>
            <span className="cp-role-badge">
              <Shield size={11} /> Employer
            </span>
          </div>
        </div>

        {/* Details Section */}
        <div className="cp-details-card">
          <div className="cp-section-header">
            <div className="cp-section-icon">
              <Store size={18} />
            </div>
            <h3>Company Information</h3>
          </div>

          <div className="cp-details-grid">
            <div className="cp-detail-item">
              <div className="cp-detail-icon">
                <Building2 size={16} />
              </div>
              <div className="cp-detail-content">
                <label>Company Name</label>
                <span className="cp-detail-value">{profile?.fullName || 'N/A'}</span>
              </div>
            </div>

            <div className="cp-detail-item">
              <div className="cp-detail-icon cp-icon-amber">
                <Mail size={16} />
              </div>
              <div className="cp-detail-content">
                <label>Email Address</label>
                <span className="cp-detail-value">{profile?.email || 'N/A'}</span>
              </div>
            </div>

            <div className="cp-detail-item">
              <div className="cp-detail-icon cp-icon-purple">
                <Calendar size={16} />
              </div>
              <div className="cp-detail-content">
                <label>Account Created</label>
                <span className="cp-detail-value">{formatDate(profile?.createdAt)}</span>
              </div>
            </div>

            <div className="cp-detail-item">
              <div className="cp-detail-icon cp-icon-green">
                <Shield size={16} />
              </div>
              <div className="cp-detail-content">
                <label>Account Status</label>
                <span className="cp-detail-value">
                  <span className="cp-status-pill">
                    <CheckCircle2 size={11} /> Active Employer
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;